import isNumber from 'lodash/isNumber';
import map from 'lodash/map';
import range from 'lodash/range';

import add from 'date-fns/add';
import isArray from 'lodash/isArray';
import isFinite from 'lodash/isFinite';
import parseISO from 'date-fns/parseISO';

import { ASSAY_RUN_STATUS } from '../constants/assay.constants';
import { dataArrayAsDataSet } from '../components/charts/helpers/dataset.helpers';
import { scientificNotationFormatter } from './formatters';

export const mapSummarizerMetadata = (summarizerMeta = {}) => ({
  type: summarizerMeta.type,
  binMode: summarizerMeta.bin_mode,
  aggregation: summarizerMeta.aggregation,
  metric: summarizerMeta.metric,
  numBins: summarizerMeta.num_bins,
  binWeights: summarizerMeta.bin_weights,
  providedEdges: summarizerMeta.provided_edges,
})

export const mapAssayDetails = (assayObj = {}) => {
  const summarizerRaw = assayObj.summarizer;
  const summarizerMetadata = summarizerRaw ? mapSummarizerMetadata(summarizerRaw) : {};

  return ({
    id: assayObj.id,
    name: assayObj.name,
    iopath: assayObj.iopath,
    active: assayObj.active,
    status: assayObj.status,
    alertThreshold: assayObj.alert_threshold,
    pipelineID: assayObj.pipeline_id,
    pipelineName: assayObj.pipeline_name,
    lastRun: assayObj.last_run,
    lastWindowStart: assayObj.last_window_start,
    baselineStart: assayObj.baseline?.Fixed?.start_at,
    baselineEnd: assayObj.baseline?.Fixed?.end_at,
    modelName: assayObj.baseline?.Fixed?.model,
    runUntil: assayObj.run_until,
    created: assayObj.created,
    updatedAt: assayObj.updated_at,
    summarizerMetadata,
    window: assayObj.window
  })
}

export const mapAssayRunSummary = (runResult = {}) => ({
  count: runResult.count,
  mean: runResult.mean,
  std: runResult.std,
  var: runResult.std * runResult.std,
  edges: runResult.edges,
  edgeNames: runResult.edge_names,
  aggregatedValues: runResult.aggregated_values,
  aggregation: runResult.aggregation,
  start: runResult.start,
  end: runResult.end,
});

export const mapAssayResults = (assayResult = {}) => {
  const summarizerRaw = assayResult.summarizer;
  const summarizerKeys = Object.keys(summarizerRaw);
  const summarizerType = summarizerKeys?.length ? summarizerKeys[0] : null;

  const summarizerMetadata = summarizerRaw ? mapSummarizerMetadata(summarizerRaw[summarizerType]) : {};
  summarizerMetadata.type = summarizerType;

  return ({
    id: assayResult.id,
    assayId: assayResult.assay_id,
    index: assayResult?.bin_index,
    baselineSummary: mapAssayRunSummary(assayResult.baseline_summary),
    windowSummary: mapAssayRunSummary(assayResult.window_summary),
    created: assayResult.created_at,
    updatedAt: assayResult.updated_at,
    analyzedAt: assayResult.analyzed_at,
    windowStartTime: assayResult.last_window_start,
    elapsedTime: assayResult.elapsed_millis,
    iopath: assayResult.iopath,
    alertThreshold: assayResult.alert_threshold,
    score: assayResult.score,
    status: assayResult.status,
    scores: assayResult.scores,
    summarizerMetadata,
  });
}

export const mapOnlyAssayResults = (assayResult = {}) => ({
  baselineSummary: mapAssayRunSummary(assayResult.baseline_summary),
  windowSummary: mapAssayRunSummary(assayResult.window_summary),
  created: assayResult.created_at,
  updatedAt: assayResult.updated_at,
  score: assayResult.score,
  status: assayResult.status,
  scores: assayResult.scores,
  summarizerMetadata: mapSummarizerMetadata(assayResult.summarizer),
});

export const newAssayConfiguration = ({
  pipelineID,
  pipelineName,
  workspaceID,
  configMap = {}
}) => ({
  name: configMap.assayName,
  pipeline_id: pipelineID,
  pipeline_name: pipelineName,
  workspace_id: workspaceID,
  iopath: configMap.iopath,
  alert_threshold: configMap.alertThreshold,
  baseline: {
    start: configMap.baselineStart,
    end: configMap.baselineEnd,
  },
  window: {
    start: configMap.windowStart,
    interval: configMap.schedulingMode,
  },
  summarizer: {
    type: configMap.aggType,
    aggregation: configMap.aggregation,
    metric: configMap.metric,
    bin_mode: configMap.binMode,
  },
})

export const joinDetailsAndRunResults = (assayList = [], resultList = []) => {
  if (!assayList?.length) {
    return assayList;
  }

  return assayList.map((rawAssayEntry) => {
    const assayEntry = mapAssayDetails(rawAssayEntry);
    const assayID = assayEntry?.id;

    const assayResults = resultList?.length ? resultList.filter(assayRun => assayRun?.assay_id === assayID).map(mapAssayResults) : [];

    return {
      ...assayEntry,
      assayResults,
    }
  })
}

export const mapAssayPointData = (assayRunResults = []) => assayRunResults?.map(runEntry => ({
  y: runEntry?.score,
  x: runEntry?.windowSummary?.start ?? runEntry?.window_summary?.start,
  status: runEntry?.status,
}));

const mapBarDataForRunSummary = runSummary => ({
  data: [...(runSummary?.aggregatedValues ?? [])],
  labels: [...(runSummary?.edgeNames ?? [])],
  count: runSummary?.count,
})

export const deriveAssayChartDatasetsByStatus = (assayName, assayPoints = []) => {
  const alertData = assayPoints?.map(({ y, status }) => status === ASSAY_RUN_STATUS.ALERT ? y : null);
  const okData = assayPoints?.map(({ y, status }) => status === ASSAY_RUN_STATUS.ALERT ? null : y);

  const okDataset = dataArrayAsDataSet(okData, 'Ok');
  const alertDataset = dataArrayAsDataSet(alertData, 'Alert');

  return [
    {
      ...okDataset,
      type: 'scatter',
      backgroundColor: '#2EA873',
      pointBackgroundColor: '#2EA873',
    },
    {
      ...alertDataset,
      type: 'scatter',
      backgroundColor: '#a83e2e',
      pointBackgroundColor: '#a83e2e',
    }
  ]
}

export const mapAssayBarData = (assayEntryWithResults) => {
  const windowChartData = mapBarDataForRunSummary(assayEntryWithResults?.windowSummary ?? assayEntryWithResults?.window_summary);
  const baselineChartdata = mapBarDataForRunSummary(assayEntryWithResults?.baselineSummary ?? assayEntryWithResults?.baseline_summary);

  return {
    window: windowChartData?.data,
    baseline: baselineChartdata?.data,
  }
}

// Default to scientific notation
export const getRoundedRangeLabel = numInputA => scientificNotationFormatter.format(numInputA)

export const mapAssayBarLabels = (assayEntryWithResults) => {
  const edgeValues = [
    ...(assayEntryWithResults?.baselineSummary?.edges ?? assayEntryWithResults?.baseline_summary?.edges ?? [])
  ];

  const edgeNames = [
    ...(assayEntryWithResults?.baselineSummary?.edgeNames ?? assayEntryWithResults?.baseline_summary?.edge_names ?? [])
  ]

  const valuesToDisplay = [
    'Min',
    ...edgeValues.splice(0, edgeValues.length - 1),
    'Max',
  ];

  const rangeOfEdges = range(valuesToDisplay.length - 1)

  return map(rangeOfEdges, (index) => {
    const edgeValue = valuesToDisplay[index];
    const nextEdgeValue = valuesToDisplay[index + 1];

    if (!isNumber(edgeValue) && isNumber(nextEdgeValue)) {
      return [`${edgeValue} - ${scientificNotationFormatter.format(nextEdgeValue)}`, edgeNames[index]]
    }

    if (isNumber(edgeValue) && !isNumber(nextEdgeValue)) {
      return [`${scientificNotationFormatter.format(edgeValue)} - ${nextEdgeValue}`, edgeNames[index]]
    }

    if (isNumber(edgeValue) && isNumber(nextEdgeValue)) {
      return [getRoundedRangeLabel(edgeValue, nextEdgeValue), edgeNames[index]];
    }

    // Unlikely default case
    return [`${edgeValue} - ${nextEdgeValue}`, edgeNames[index]];
  })
}

export const deriveEmptyEdgesArrayFromNumberOfBins = (numberOfBins = 1) => range(numberOfBins + 1).map(() => null);
export const deriveEmptyWeightsArrayFromNumberOfBins = (numberOfBins = 1) => range(numberOfBins + 2).map(() => null);

export const padEndOfArrayWithNulls = (inputArr = [], numNullValues = 1) => {
  const nulls = range(numNullValues).map(() => null)

  return [
    ...inputArr,
    ...nulls
  ]
};

// Num Edges = Num Bins + 1
// Num Weights = Num Bins + 2 = Number of Table Rows
export const transformBinEdgesToTableData = (binEdges = [], binWeights = []) => {
  const numberOfWeights = binWeights.length;
  return binWeights.map((weightValue, index) => {
    const binLabel = index === 0 ? 'Left Outlier' : index === numberOfWeights - 1 ? 'Right Outlier' : `Bin ${index}`;
    const binWeight = binWeights?.length > 0 ? binWeights[index] : undefined;
    const fromValue = index === 0 ? 'Min' : binEdges[index - 1];
    const toValue = index === numberOfWeights - 1 ? 'Max' : binEdges[index];
    return ({
      binLabel,
      binWeight,
      fromValue,
      toValue,
    })
  });
}

export const areEdgesRequiredAndIncomplete = (assayConfig) => {
  const binMode = assayConfig.summarizer.bin_mode;
  const binEdges = assayConfig.summarizer.provided_edges;
  return binMode === 'Provided' && (!binEdges.length || binEdges.includes(null))
}

export const areWeightsRequiredAndIncomplete = (assayConfig) => {
  const binWeights = assayConfig.summarizer.bin_weights;

  return binWeights?.length && binWeights.includes(null);
}

// Checks to make sure that edge values are given in the expected order
export const areEdgeValuesInappropriate = (assayConfig) => {
  const binMode = assayConfig.summarizer.bin_mode;
  if (binMode !== 'Provided') {
    return false;
  }

  const binEdges = assayConfig.summarizer.provided_edges?.length ? [...assayConfig.summarizer.provided_edges] : [];
  const originalEdges = [...binEdges];
  const sortedEdges = binEdges.sort();

  return !sortedEdges.every((value, index) => value === originalEdges[index]);
}

export const deriveLabelsForBucketData = (buckets = []) => buckets.map(bucket => getRoundedRangeLabel(bucket[0], bucket[bucket.length - 1]))

// get all output 0 0. TODO: Break iopath into more chunks, maybe in MI?
export const getBaselineSamplesFromResponse = (baselineResponse, iopath, useArrowLogFormat) => map(baselineResponse, (log) => {
  const parts = iopath.split(' ')
  if (useArrowLogFormat) {
    const prefix = parts[0] === 'output' ? 'out' : 'in'
    const name = `${prefix}.${parts[1]}`
    const index = parseInt(parts[2], 10)
    const path = log[name];

    if (isFinite(path)) {
      return path;
    }
    
    if (isArray(path)) {
      return path[index];
    }

    // Intentional warning
    // eslint-disable-next-line no-console
    console.warn('Baselines of this shape are not supported.');
    return null;
  }
  const name = parts[1]
  const index = parseInt(parts[2], 10)
  return parts[0] === 'output' ? log.outputs[name].Float.data[index] : log.original_data.tensor[name][index]
})

const calculateIQR = (arr) => {
  const sorted = arr.sort((a, b) => a - b);
  const len = arr.length;
  const n = (len % 2 === 0) ? len / 2 : (len / 2) + 1;
  const q1MedianFloor = Math.floor(n / 2);
  const q3MedianFloor = Math.floor(n * 1.5);
  const q1 = (n % 2 === 0) ? (sorted[q1MedianFloor] + sorted[q1MedianFloor + 1]) / 2 : sorted[q1MedianFloor + 1];
  const q3 = (n % 2 === 0) ? (sorted[q3MedianFloor] + sorted[q3MedianFloor + 1]) / 2 : sorted[q3MedianFloor + 1];
  return q3 - q1;
}

export const freedmanDiaconisBinDetection = (arr) => {
  const IQR = calculateIQR(arr);
  const denom = Math.cbrt(arr.length);
  return 2 * IQR / denom;
}

// Of the form `{num} {duration}`
const parseWindowWidth = (windowWidth) => {
  const [numeric, durationStr] = windowWidth.split(' ');

  // Is this ever not an int?
  const num = parseFloat(numeric);
  switch (durationStr) {
  case 'minutes':
  case 'minute':
    return { 'minutes': num };
  case 'hours':
  case 'hour':
    return { 'hours': num };
  case 'days':
  case 'day':
    return { 'days': num };
  case 'weeks':
  case 'week':
  default:
    return { 'weeks': num };
  }
}


/* 
 * 1. If last_window_start, return last_window_start + window width
 * 2. ElseIf window.start, return window.start
 * 3. Else, return baseline.end_at
 * 
 * Note, 3 will need expounding if more Baseline types are supported.
 */
const calculateLastWindowEnd = (assayConfig) => {
  if (assayConfig.lastWindowStart && assayConfig.window?.width) {
    const windowWidthObj = parseWindowWidth(assayConfig.window.width);
    const lastWindowStartDate = parseISO(assayConfig.lastWindowStart);

    return add(lastWindowStartDate, windowWidthObj);
  }
  
  // This assay hasn't been run yet, but this is the intended start time.
  if (assayConfig.window?.start) {
    return parseISO(assayConfig.window.start);
  }

  // There's no start time, so use the baseline's end time.
  return parseISO(assayConfig.baselineEnd);
};

// NextRun is no longer a field supported by MI. It is now calculated by the following alg:
// last_window_end + window.width = next_run
// where
// last_window_end = last_window_start + window.width
export const calculateNextRun = (assayConfig) => {
  const windowWidthObj = parseWindowWidth(assayConfig.window.width);
  const lastWindowEnd = calculateLastWindowEnd(assayConfig);
  return add(lastWindowEnd, windowWidthObj);
}
